import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Button from './Button';
import { BRAND, BRAND_SHORT, brandify } from '../brandConfig';

const PricingPage = ({ navigate }) => {
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // 'monthly' or 'yearly'
  const [processingSocialPlan, setProcessingSocialPlan] = useState('');
  const [socialError, setSocialError] = useState('');

  const handleSocialCheckout = async (planTitle) => {
    setProcessingSocialPlan(planTitle);
    setSocialError('');
    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planName: `Social Media - ${planTitle}`,
          paymentType: 'fixed_subscription',
        }),
      });
      const session = await response.json();
      if (response.ok && session.url) {
        window.location.href = session.url;
      } else {
        setSocialError(session.error || 'Failed to start checkout. Please try again.');
        setProcessingSocialPlan('');
      }
    } catch (err) {
      setSocialError('Network error. Please try again.');
      setProcessingSocialPlan('');
    }
  };

  const plans = [
    {
      tag: 'BRAND IDENTITY',
      payId: 'logo',
      title: 'Logo Design',
      oneTime: true,
      oneTimePrice: '$54.99',
      features: [
        '1 custom logo design',
        'High-resolution files (PNG + SVG)',
        'Transparent background versions',
        'Full commercial usage rights'
      ],
      guardrail: 'One-time payment, no subscription. Includes 1 revision round. Additional concepts or revision rounds billed separately.',
      highlighted: false,
    },
    {
      tag: 'ESSENTIAL BASE',
      payId: 'essential',
      title: 'Core Website',
      setup: '$299',
      monthlyPrice: '$59',
      yearlyPrice: '$49',
      yearlyTotal: '$588/yr',
      features: [
        'New website with 4 pages max',
        '1 custom domain setup',
        'Basic responsive design for all devices',
        'Contact form integration & basic security'
      ],
      guardrail: 'Infrastructure support only. 1 cosmetic change included. (Overages billed separately at premium rate). Domain purchase price is not included (the free domain provided is a Vercel domain).',
      highlighted: false,
    },
    {
      tag: 'MOST POPULAR',
      payId: 'pro',
      title: 'Pro Website',
      setup: '$499',
      monthlyPrice: '$99',
      yearlyPrice: '$83',
      yearlyTotal: '$996/yr',
      features: [
        'New website with 4+ pages',
        'SEO optimized',
        'AI optimized',
        'Priority ongoing content updates',
        'Premium image sourcing & curation',
        'Offer popup integration',
        'Custom domain included',
        'Optimization for max speed'
      ],
      guardrail: 'Includes 1 hr/mo for quick text/technical edits. Max 3 design iterations; cosmetic overages billed hourly. Domain purchase price is not included (the free domain provided is a Vercel domain).',
      highlighted: true,
    },
    {
      tag: 'WORK SMARTER',
      payId: 'scale',
      title: 'Automation',
      setup: '$499',
      monthlyPrice: '$149',
      yearlyPrice: '$119',
      yearlyTotal: '$1,428/yr',
      features: [
        '1 automated process',
        'Production grade automation',
        '1 data integration (e.g., Google Sheets to email)',
        'Custom-triggered event configuration'
      ],
      guardrail: 'Applies strictly to locked backend data infrastructure workflows. UI updates or client visual edits excluded.',
      highlighted: false,
    }
  ];

  return (
    <div className="py-32 max-w-[95vw] mx-auto px-4 md:px-8">
      <Helmet>
        <title>{`Small Business Website Cost & Design Packages | ${BRAND}`}</title>
        <meta name="description" content={brandify('How much does a small business website cost? Wind Marketing website design packages start at $299 setup + $59/month — affordable web design for small business with transparent, no-surprise pricing.')} />
        <meta name="keywords" content={`how much does a small business website cost, website design packages for small business, affordable web design for small business, ${BRAND_SHORT} pricing, web design cost`} />
        <link rel="canonical" href="https://www.wind-marketing.com/pricing" />
        <meta property="og:title" content={`Small Business Website Cost & Design Packages • ${BRAND}`} />
        <meta property="og:description" content={brandify('Wind Marketing offers transparent pricing for web design, automation, and marketing services. Plans start at $59/month.')} />
        <meta property="og:url" content="https://www.wind-marketing.com/pricing" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How much does a small business website cost?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "${brandify('A professionally designed small business website typically costs anywhere from $500 to $10,000+ upfront with traditional agencies. Wind Marketing uses a subscription model instead: the Core Website plan is $299 setup plus $59/month for a custom 4-page responsive site, and the Pro Website plan is $499 setup plus $99/month with full SEO and AI optimization, a custom domain, and priority content updates included. Annual billing saves 20%.')}"
                }
              },
              {
                "@type": "Question",
                "name": "How much does ${BRAND} charge for web design?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "${brandify("Wind Marketing's Core Website plan starts at $59/month with a $299 setup fee, covering a 4-page responsive website. The Pro Website plan is $99/month with a $499 setup fee and includes SEO optimization, AI optimization, custom domain, premium image sourcing, and priority content updates.")}"
                }
              },
              {
                "@type": "Question",
                "name": "Does ${BRAND} offer annual billing discounts?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "${brandify('Yes. Wind Marketing offers 20% off when you choose annual billing. For example, the Pro Website plan drops from $99/month to $83/month (billed as $996/year) on an annual plan.')}"
                }
              },
              {
                "@type": "Question",
                "name": "How much does social media management cost at ${BRAND}?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "${brandify("Wind Marketing's social media plans start at $50/month for one post every two weeks, $95/month for weekly posts, $180/month for two posts per week, or $595/month for daily posting. All plans include caption writing, hashtag research, direct scheduling to your Facebook or Instagram page, and a custom graphic template matched to your brand. No long-term contracts.")}"
                }
              },
              {
                "@type": "Question",
                "name": "How much does a custom logo design cost at ${BRAND}?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "${brandify('Wind Marketing offers custom logo design for a one-time payment of $54.99, which includes one custom logo, high-resolution PNG and SVG files, transparent background versions, full commercial usage rights, and one revision round. No subscription required.')}"
                }
              }
            ]
          }
        `}</script>
      </Helmet>
      {/* Header */}
      <div className="text-center mb-16">
        <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold uppercase tracking-tighter leading-none mb-6 pt-[60px] md:pt-0 text-foreground">
          PRICING PLANS
        </h1>
        <p className="text-[19.5px] md:text-xl lg:text-2xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-tight">
          Clear scope. Zero fluff. Engineered pricing built to scale your business operations.
        </p>


        {/* Interactive Billing Toggle */}
        <div className="inline-flex border-2 border-border p-1 bg-background select-none">
          <button
            onClick={() => setBillingPeriod('monthly')}
            className={`px-6 py-2 uppercase tracking-tighter font-bold transition-all duration-200 text-sm ${
              billingPeriod === 'monthly'
                ? 'bg-accent text-accent-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Bill Monthly
          </button>
          <button
            onClick={() => setBillingPeriod('yearly')}
            className={`px-6 py-2 uppercase tracking-tighter font-bold transition-all duration-200 text-sm ${
              billingPeriod === 'yearly'
                ? 'bg-accent text-accent-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Bill Annually (Save 20%)
          </button>
        </div>
      </div>

      {/* Grid Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 items-stretch">
        {plans.map((plan, index) => {
          const isYearly = billingPeriod === 'yearly';
          const price = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
          
          return (
            <div 
              key={index}
              className={`flex flex-col relative h-full transition-all duration-300 border-2 ${
                plan.highlighted 
                  ? 'border-accent bg-background shadow-[8px_8px_0px_#0101ff] z-10' 
                  : 'border-border bg-background hover:border-foreground hover:shadow-[6px_6px_0px_#3F3F46]'
              }`}
            >
              {/* Highlight Badge */}
              {plan.highlighted && (
                <div className="absolute top-0 left-0 right-0 transform -translate-y-1/2 flex justify-center">
                  <span className="bg-accent text-accent-foreground text-xs uppercase tracking-widest font-black px-4 py-1 border-2 border-accent">
                    {plan.tag}
                  </span>
                </div>
              )}

              {/* Plan Inner Wrapper */}
              <div className="p-8 md:p-10 flex flex-col flex-grow justify-between gap-8">
                {/* Upper Content */}
                <div className="space-y-6">
                  {/* Category Tag (Non-highlighted plans) */}
                  {!plan.highlighted && (
                    <span className="text-xs uppercase tracking-widest font-bold text-muted-foreground block">
                      {plan.tag}
                    </span>
                  )}
                  {plan.highlighted && (
                    <span className="text-xs uppercase tracking-widest font-bold text-accent block">
                      RECOMMENDED ARCHITECTURE
                    </span>
                  )}

                  {/* Title */}
                  <h3 className="text-2xl md:text-3xl font-extrabold uppercase tracking-tighter leading-none" style={{ fontFamily: "'Poppins Black', sans-serif" }}>
                    {plan.title}
                  </h3>

                  {/* Divider */}
                  <div className="h-0.5 w-12 bg-accent"></div>

                  {/* Price */}
                  <div className="space-y-2">
                    {plan.oneTime ? (
                      <>
                        <div className="flex items-baseline gap-1">
                          <span className="text-4xl md:text-5xl font-black tracking-tighter">{plan.oneTimePrice}</span>
                          <span className="text-sm font-bold text-muted-foreground">ea</span>
                        </div>
                        <div className="text-xs text-muted-foreground uppercase tracking-wide">
                          One-time payment
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-baseline gap-1">
                          <span className="text-4xl md:text-5xl font-black tracking-tighter">{price}</span>
                          <span className="text-sm font-bold text-muted-foreground">/mo</span>
                        </div>
                        {isYearly && (
                          <span className="block text-xs font-black text-accent uppercase tracking-wider">
                            Billed annually ({plan.yearlyTotal})
                          </span>
                        )}
                        <div className="text-xs text-muted-foreground uppercase tracking-wide">
                          Upfront Setup: <strong className="text-foreground">{plan.setup}</strong>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 pt-4 border-t border-muted">
                    {plan.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-2 text-sm text-foreground/90">
                        <span className="text-accent font-bold mt-0.5 select-none">■</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Lower Content */}
                <div className="space-y-6 pt-6 border-t border-muted">
                  {/* Scope Guardrail */}
                  <div className="bg-muted border border-border p-3">
                    <span className="text-[9px] font-black tracking-widest text-foreground uppercase block mb-0.5">
                      Additional Information
                    </span>
                    <p className="text-[10px] text-muted-foreground leading-normal">
                      {plan.guardrail}
                    </p>
                  </div>

                  {/* CTA Button */}
                  <Button
                    variant={plan.highlighted ? 'primary' : 'outline'}
                    className="w-full"
                    onClick={() => {
                      navigate('/pay', { plan: plan.payId });
                    }}
                  >
                    Select Plan
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Social Media Pricing ─────────────────────────────────────────── */}
      <div id="social-pricing" className="mt-32 scroll-mt-24">
        <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold uppercase tracking-tighter leading-none mb-4 text-foreground text-center">
          SOCIAL MEDIA
        </h2>
        <p className="text-[19.5px] md:text-xl text-muted-foreground max-w-2xl mx-auto mb-4 leading-tight text-center font-bold uppercase tracking-wide">
          Simple, Hands-Free Posting
        </p>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-16 leading-relaxed text-center">
          We handle the strategy, creation, and scheduling. You choose the platform and format that fits your business goals.
        </p>

        {/* Social Pricing Grid */}
        <div>
          {socialError && (
            <div className="bg-red-900/20 border border-red-500/50 p-4 mb-8 max-w-2xl mx-auto">
              <p className="text-red-400 text-sm font-bold text-center">{socialError}</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 items-stretch">
            {[
              {
                tag: 'DIP YOUR TOES',
                title: 'Basic',
                price: '$50',
                features: [
                  '1 post every 2 weeks (2/mo)',
                  'Picture or Carousel posts',
                  'Facebook or Instagram',
                  'Caption writing & hashtag research'
                ],
                guardrail: 'End-to-end management with direct scheduling to your page. Custom graphic template included. No long-term contract.',
                highlighted: false,
              },
              {
                tag: 'BUILD MOMENTUM',
                title: 'Starter',
                price: '$95',
                features: [
                  '1 post per week (4/mo)',
                  'Picture or Carousel posts',
                  'Facebook or Instagram',
                  'Caption writing & hashtag research'
                ],
                guardrail: 'End-to-end management with direct scheduling to your page. Custom graphic template included. No long-term contract.',
                highlighted: false,
              },
              {
                tag: 'MOST POPULAR',
                title: 'Growth',
                price: '$180',
                features: [
                  '2 posts per week (8/mo)',
                  'Picture, Carousel, or Mix',
                  'Facebook or Instagram',
                  'Caption writing & hashtag research'
                ],
                guardrail: 'End-to-end management with direct scheduling to your page. Custom graphic template included. No long-term contract.',
                highlighted: true,
              },
              {
                tag: 'MAXIMUM PRESENCE',
                title: 'Pro',
                price: '$595',
                features: [
                  '1 post every day (30/mo)',
                  'Picture, Carousel, or Mix',
                  'Facebook or Instagram',
                  'Caption writing & hashtag research'
                ],
                guardrail: 'End-to-end management with direct scheduling to your page. Custom graphic template included. No long-term contract.',
                highlighted: false,
              },
            ].map((plan, index) => (
              <div
                key={index}
                className={`flex flex-col relative h-full transition-all duration-300 border-2 ${
                  plan.highlighted
                    ? 'border-accent bg-background shadow-[8px_8px_0px_#0101ff] z-10'
                    : 'border-border bg-background hover:border-foreground hover:shadow-[6px_6px_0px_#3F3F46]'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute top-0 left-0 right-0 transform -translate-y-1/2 flex justify-center">
                    <span className="bg-accent text-accent-foreground text-xs uppercase tracking-widest font-black px-4 py-1 border-2 border-accent">
                      {plan.tag}
                    </span>
                  </div>
                )}
                <div className="p-8 md:p-10 flex flex-col flex-grow justify-between gap-8">
                  <div className="space-y-6">
                    {!plan.highlighted && (
                      <span className="text-xs uppercase tracking-widest font-bold text-muted-foreground block">
                        {plan.tag}
                      </span>
                    )}
                    {plan.highlighted && (
                      <span className="text-xs uppercase tracking-widest font-bold text-accent block">
                        RECOMMENDED CADENCE
                      </span>
                    )}
                    <h3 className="text-2xl md:text-3xl font-extrabold uppercase tracking-tighter leading-none" style={{ fontFamily: "'Poppins Black', sans-serif" }}>
                      {plan.title}
                    </h3>
                    <div className="h-0.5 w-12 bg-accent"></div>
                    <div className="space-y-2">
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl md:text-5xl font-black tracking-tighter">{plan.price}</span>
                        <span className="text-sm font-bold text-muted-foreground">/mo</span>
                      </div>
                      <div className="text-xs text-muted-foreground uppercase tracking-wide">
                        No setup fee
                      </div>
                    </div>
                    <ul className="space-y-3 pt-4 border-t border-muted">
                      {plan.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-2 text-sm text-foreground/90">
                          <span className="text-accent font-bold mt-0.5 select-none">■</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="space-y-6 pt-6 border-t border-muted">
                    <div className="bg-muted border border-border p-3">
                      <span className="text-[9px] font-black tracking-widest text-foreground uppercase block mb-0.5">
                        Additional Information
                      </span>
                      <p className="text-[10px] text-muted-foreground leading-normal">
                        {plan.guardrail}
                      </p>
                    </div>
                    <Button
                      variant={plan.highlighted ? 'primary' : 'outline'}
                      className="w-full"
                      disabled={processingSocialPlan === plan.title}
                      onClick={() => handleSocialCheckout(plan.title)}
                    >
                      {processingSocialPlan === plan.title ? 'PROCESSING...' : 'Get Started'}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Microservices Pricing ────────────────────────────────────────── */}
      <div id="microservices-pricing" className="mt-32 max-w-5xl mx-auto scroll-mt-24">
        <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold uppercase tracking-tighter leading-none mb-4 text-foreground text-center">
          MICROSERVICES
        </h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-12 leading-relaxed text-center">
          Custom backend systems, APIs, payment integrations, and data pipelines — built to spec.
        </p>
        <div className="border-2 border-border p-8 md:p-12 md:flex items-center justify-between gap-12">
          <div className="mb-8 md:mb-0">
            <span className="text-xs uppercase tracking-widest font-bold text-accent block mb-4">CUSTOM SCOPE</span>
            <h3 className="text-2xl md:text-3xl font-extrabold uppercase tracking-tighter mb-4 text-foreground" style={{ fontFamily: "'Poppins Black', sans-serif" }}>
              Scoped &amp; Priced Upfront
            </h3>
            <p className="text-muted-foreground leading-relaxed mb-6 max-w-xl">
              Every microservice project is different, so we quote each one individually — a fixed price and timeline before any work begins, no hourly billing, no scope creep. Projects start at <strong className="text-foreground">$499</strong>.
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-sm text-foreground/90">
                <span className="text-accent font-bold mt-0.5 select-none">■</span>
                <span>Custom APIs &amp; third-party integrations</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground/90">
                <span className="text-accent font-bold mt-0.5 select-none">■</span>
                <span>Payment &amp; checkout systems</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground/90">
                <span className="text-accent font-bold mt-0.5 select-none">■</span>
                <span>Data pipelines, dashboards &amp; reporting</span>
              </li>
            </ul>
          </div>
          <div className="flex-shrink-0 text-center">
            <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">Starting at</div>
            <div className="text-5xl md:text-6xl font-black tracking-tighter text-foreground mb-6">$499</div>
            <Button
              size="large"
              onClick={() => navigate('/contact', { serviceInterest: 'microservices' })}
            >
              Request a Quote
            </Button>
          </div>
        </div>
      </div>
      {/* Citable answer block: how much does a small business website cost */}
      <div className="border-2 border-border p-6 mt-32">
        <h2 className="text-xl md:text-2xl font-bold uppercase tracking-tighter mb-3 text-foreground">
          How Much Does a Small Business Website Cost?
        </h2>
        <p className="text-lg text-muted-foreground leading-relaxed">
          Traditional agencies charge $500 to $10,000+ upfront for a small business website, then bill hourly for every change. {BRAND} uses transparent subscription pricing instead: the Core Website plan is <strong className="text-foreground">$299 setup + $59/month</strong> for a custom 4-page responsive site, and the Pro Website plan is <strong className="text-foreground">$499 setup + $99/month</strong> with full SEO and AI optimization, a custom domain, premium images, and priority content updates included. Annual billing saves 20%. Design, development, contact forms, and ongoing support are all part of the package — affordable web design for small business without surprise invoices.
        </p>
      </div>
    </div>
  );
};

export default PricingPage;
